import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cog8ToothIcon } from '@heroicons/react/24/outline';
import { buildJob, buildOriginTestJob, buildScrapTestJob, findUnsupportedCharacter, getScrapTestPreviewBounds } from './gcode';
import { allConfirmed, getEngravingState, itemBadge, mainActionLabel } from './engravingState';
import { Laser } from './laser';
import { buildFillJob } from './lp4';
import { drawPreview } from './preview';
import montserratFont from './fonts/Montserrat-400.ttf';
import cormorantFont from './fonts/CormorantGaramond-400.ttf';
import scriptFont from './fonts/PetitFormalScript-400.ttf';
import './EngravingWorkstation.css';

const API = '/api/v1/warehouse/engraving';
const FONT_URLS = {
  Montserrat: montserratFont,
  'Cormorant Garamond': cormorantFont,
  'Petit Formal Script': scriptFont,
};
const DEFAULT_PRESET = {
  laser: 'blue',
  power_pct: 30,
  depth_pct: 10,
  passes: 1,
  line_interval_mm: 0.08,
  origin_x_mm: 80,
  origin_y_mm: 60,
  text_height_mm: 4,
  max_width_mm: 40,
  rotation_deg: 0,
  mirror_x: false,
  mirror_y: true,
  outline_pass: true,
};
const PRESET_FIELDS = [
  ['power_pct', 'Power (%)', 'number'],
  ['depth_pct', 'Depth (%)', 'number'],
  ['passes', 'Passes', 'number'],
  ['origin_x_mm', 'Origin X (mm)', 'number'],
  ['origin_y_mm', 'Origin Y (mm)', 'number'],
  ['text_height_mm', 'Text height (mm)', 'number'],
  ['max_width_mm', 'Max width (mm)', 'number'],
  ['rotation_deg', 'Rotation (°)', 'number'],
  ['mirror_x', 'Mirror X', 'checkbox'],
  ['mirror_y', 'Mirror Y', 'checkbox'],
];
const fontBufferCache = new Map();
const COLOURS = {
  Sapphire: '#1f3f73',
  Gunmetal: '#4a4d52',
  Saffron: '#c8862d',
  Bourbon: '#7a3f1d',
  'Onyx Black': '#1b1b1b',
  Sage: '#8a9a7b',
  'Blue Haven': '#2f5d7c',
  'Moonlit Onyx': '#1b1b1b',
  'Midnight Ember': '#3a2622',
};
const itemKey = (item) => `${item.orderName}:${item.lineItemId}:${item.unit}`;
const fontClass = (font) => ({
  Montserrat: 'engraving-font-montserrat',
  'Cormorant Garamond': 'engraving-font-cormorant',
  'Petit Formal Script': 'engraving-font-script',
}[font] || 'engraving-font-montserrat');
const formatPortId = (value) => Number.isInteger(value) ? `0x${value.toString(16).padStart(4, '0')}` : 'unknown';
const escapeRawReply = (reply) => Array.from(reply, (character) => {
  const code = character.charCodeAt(0);
  if (code === 13) return '\\r';
  if (code === 10) return '\\n';
  if (code < 32 || (code >= 127 && code <= 159)) return `\\x${code.toString(16).padStart(2, '0')}`;
  return character;
}).join('').replace(/ +$/g, (spaces) => '\\x20'.repeat(spaces.length));

const formatLaserDiagnostics = (diagnostics) => {
  if (Array.isArray(diagnostics.steps)) return JSON.stringify(diagnostics, null, 2);
  const portInfo = diagnostics.portInfo || {};
  return [
    `Port VID ${formatPortId(portInfo.usbVendorId)} · PID ${formatPortId(portInfo.usbProductId)}`,
    `Baud ${diagnostics.baudRate} · DTR ${diagnostics.dtr ? 'on' : 'off'}`,
    `Status query: ${diagnostics.statusQuery === null ? 'none answered (Engrave locked)' : `${diagnostics.statusQuery} (auto-detected)`}`,
    `Previewing: ${diagnostics.previewing ? 'yes' : 'no'}`,
    `Last preview frame: ${diagnostics.lastPreviewFrame || '(none)'}`,
    `Last preview stop reply: ${diagnostics.lastPreviewStopReply || '(none)'}`,
    ...(diagnostics.queries || []).map(({ query, outcome, ms, bytes }) => `${query} → ${outcome === 'timeout' ? `timeout after ${ms} ms` : `${outcome} after ${ms} ms`} · ${bytes} bytes`),
    `Total bytes received since connect: ${diagnostics.totalBytes}`,
    `Text: ${diagnostics.text || '(empty)'}`,
    `Hex: ${diagnostics.hex || '(empty)'}`,
    `Pending buffer: ${diagnostics.pendingBuffer || '(empty)'}`,
    `Raw replies:\n${diagnostics.rawReplies.length ? diagnostics.rawReplies.map(escapeRawReply).join('\n') : '(none)'}`,
  ].join('\n');
};

const apiRequest = async (path, options = {}) => {
  const response = await fetch(`${API}${path}`, {
    credentials: 'include',
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return body;
};

const loadFontBuffer = async (fontName) => {
  const url = FONT_URLS[fontName] || FONT_URLS.Montserrat;
  if (!fontBufferCache.has(url)) {
    fontBufferCache.set(url, fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Could not load ${fontName} font.`);
      return response.arrayBuffer();
    }));
  }
  return fontBufferCache.get(url);
};

const EngravingWorkstation = ({ canEdit = false }) => {
  const scanInputRef = useRef(null);
  const previewRef = useRef(null);
  const closeRef = useRef(null);
  const laserRef = useRef(null);
  const jobRef = useRef(null);
  const startingRef = useRef(false);
  const infoReadingRef = useRef(false);
  const previewingRef = useRef(false);
  const [stationName, setStationName] = useState(() => localStorage.getItem('warehouse-engraving-station') || 'Station 2');
  const [fillDpi, setFillDpi] = useState(() => localStorage.getItem('warehouse-engraving-fill-dpi') === '508' ? 508 : 254);
  const [laserSnapshot, setLaserSnapshot] = useState({ connected: false, busy: false, previewing: false, machineState: null, rawReplies: [] });
  const [orders, setOrders] = useState([]);
  const [historyUnavailable, setHistoryUnavailable] = useState(false);
  const [tote, setTote] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [glyphError, setGlyphError] = useState('');
  const [glyphLoading, setGlyphLoading] = useState(false);
  const [laserIdle, setLaserIdle] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [presetDraft, setPresetDraft] = useState(DEFAULT_PRESET);
  const [savedState, setSavedState] = useState('');
  const [infoBusy, setInfoBusy] = useState(false);
  const [motionCheckBusy, setMotionCheckBusy] = useState(false);
  const [laserInfo, setLaserInfo] = useState(null);
  const [copyState, setCopyState] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [resultSaving, setResultSaving] = useState(false);

  const items = useMemo(() => orders.flatMap((order) =>
    (order.items || []).map((item) => ({ ...item, orderName: order.orderName }))), [orders]);
  const selectedItem = items.find((item) => itemKey(item) === selectedKey) || null;
  const engravingCount = items.length;
  const remainingCount = items.filter((item) => getEngravingState(item) !== 'confirmed').length;
  const currentPreset = selectedItem?.preset || null;
  const activeConfirmation = selectedItem && (
    confirmation?.key === itemKey(selectedItem)
      ? confirmation
      : getEngravingState(selectedItem) === 'unconfirmed' && selectedItem.lastAttemptId
        ? { key: itemKey(selectedItem), attemptId: selectedItem.lastAttemptId }
        : null
  );
  const laserInfoText = useMemo(() => laserInfo ? formatLaserDiagnostics(laserInfo) : '', [laserInfo]);
  const draftHasChanges = selectedItem
    ? JSON.stringify(presetDraft) !== JSON.stringify({ ...DEFAULT_PRESET, ...(selectedItem.preset || {}) })
    : true;

  const syncLaser = useCallback((laser) => {
    const diagnostics = laser.getDiagnostics();
    setLaserSnapshot({
      connected: diagnostics.connected,
      busy: diagnostics.busy,
      previewing: diagnostics.previewing,
      machineState: diagnostics.machineState,
      rawReplies: diagnostics.rawReplies.slice(-12),
    });
    if (previewingRef.current && !diagnostics.previewing) {
      setLaserInfo(diagnostics);
      setCopyState('');
    }
    previewingRef.current = diagnostics.previewing;
  }, []);

  useEffect(() => {
    const laser = new Laser();
    laserRef.current = laser;
    const unsubscribe = laser.subscribe(syncLaser);
    syncLaser(laser);
    const stopPreviewOnUnload = () => {
      if (laser.previewing) laser.stopPreview().catch(() => {});
    };
    window.addEventListener('beforeunload', stopPreviewOnUnload);
    const focusTimer = window.setTimeout(() => scanInputRef.current?.focus(), 200);
    return () => {
      window.removeEventListener('beforeunload', stopPreviewOnUnload);
      if (laser.previewing) laser.stopPreview().catch(() => {});
      window.clearTimeout(focusTimer);
      unsubscribe();
    };
  }, [syncLaser]);

  useEffect(() => {
    localStorage.setItem('warehouse-engraving-station', stationName);
  }, [stationName]);

  useEffect(() => {
    const preset = selectedItem?.preset;
    setPresetDraft({ ...DEFAULT_PRESET, ...(preset || {}) });
    setSavedState('');
  }, [selectedItem?.lineItemId, selectedItem?.unit, selectedItem?.handle, selectedItem?.preset]);

  useEffect(() => {
    let active = true;
    setGlyphError('');
    if (!selectedItem?.text) {
      setGlyphLoading(false);
      return () => { active = false; };
    }
    setGlyphLoading(true);
    loadFontBuffer(selectedItem.font).then((fontBuffer) => {
      if (!active) return;
      const unsupported = findUnsupportedCharacter(fontBuffer, selectedItem.text);
      setGlyphError(unsupported ? `This font can't engrave '${unsupported}'` : '');
    }).catch((error) => {
      if (active) setGlyphError(error.message || 'Font could not be loaded.');
    }).finally(() => {
      if (active) setGlyphLoading(false);
    });
    return () => { active = false; };
  }, [selectedItem?.text, selectedItem?.font]);

  useEffect(() => {
    if (!selectedItem?.placement || !selectedItem.previewImage) return;
    const draw = (canvas, view) => {
      if (canvas) drawPreview(canvas, selectedItem.text, selectedItem.font, selectedItem.placement, selectedItem.previewImage, { view });
    };
    draw(previewRef.current, 'full');
    draw(closeRef.current, 'close');
  }, [selectedItem]);

  const focusScan = () => window.setTimeout(() => scanInputRef.current?.focus(), 0);

  const refreshIdle = useCallback(async () => {
    if (infoReadingRef.current) return;
    const laser = laserRef.current;
    if (!laser?.connected || laser.busy || jobRef.current) {
      setLaserIdle(false);
      return;
    }
    try {
      setLaserIdle(await laser.isFileIdle());
    } catch {
      setLaserIdle(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(refreshIdle, 1800);
    return () => window.clearInterval(timer);
  }, [refreshIdle]);

  const loadTote = async (event) => {
    event?.preventDefault();
    const code = barcode.trim();
    if (!code) return;
    setLoading(true);
    setFeedback('');
    try {
      const result = await apiRequest(`/tote/${encodeURIComponent(code)}?station=${encodeURIComponent(stationName)}`);
      let presetRows = [];
      try {
        const presets = await apiRequest(`/presets?station=${encodeURIComponent(stationName)}`);
        presetRows = Array.isArray(presets) ? presets : (presets.presets || []);
      } catch (_error) {
        presetRows = [];
      }
      const nextOrders = (result.orders || []).map((order) => ({
        ...order,
        items: (order.items || []).map((item) => ({
          ...item,
          preset: item.preset || presetRows.find((preset) => preset.product_handle === item.handle) || null,
        })),
      }));
      setTote(result.tote);
      setHistoryUnavailable((result.orders || []).some((order) => order.historyUnavailable));
      setOrders(nextOrders);
      const first = nextOrders.flatMap((order) => (order.items || []).map((item) => ({ ...item, orderName: order.orderName })))
        .find((item) => getEngravingState(item) !== 'confirmed');
      setSelectedKey(first ? itemKey(first) : null);
      setConfirmation(null);
      setBarcode('');
    } catch (error) {
      setTote(null);
      setHistoryUnavailable(false);
      setOrders([]);
      setSelectedKey(null);
      setConfirmation(null);
      setFeedback(error.status === 503
        ? 'Add the station key to load orders'
        : error.message || 'Could not load this tote.');
    } finally {
      setLoading(false);
      focusScan();
    }
  };

  const pairLaser = async (showAll = false) => {
    try {
      await laserRef.current?.requestPort(showAll);
      setFeedback('');
      await refreshIdle();
    } catch (error) {
      if (error.name !== 'NotFoundError') setFeedback(error.message || 'Could not pair the laser.');
    } finally {
      focusScan();
    }
  };

  const readLaserInfo = async () => {
    if (!laserRef.current?.connected) return;
    infoReadingRef.current = true;
    setInfoBusy(true);
    try {
      setLaserInfo(await laserRef.current.readInfo());
      setCopyState('');
    } catch (error) {
      setFeedback(error.message || 'Could not read laser information.');
    } finally {
      infoReadingRef.current = false;
      setInfoBusy(false);
      await refreshIdle();
      focusScan();
    }
  };

  const runMotionCheck = async () => {
    const laser = laserRef.current;
    if (!laser?.connected || laser.busy || jobRef.current || startingRef.current) return;
    if (!window.confirm("Moves the laser head with the beam OFF. No engraving. Keep Stop ready. Continue?")) return;
    infoReadingRef.current = true;
    setInfoBusy(true);
    setMotionCheckBusy(true);
    try {
      setLaserInfo(await laser.motionCheck());
      setCopyState('');
    } catch (error) {
      setFeedback(error.message || 'Could not run the laser motion check.');
    } finally {
      infoReadingRef.current = false;
      setInfoBusy(false);
      setMotionCheckBusy(false);
      await refreshIdle();
      focusScan();
    }
  };

  const stopMotionCheck = async () => {
    if (motionCheckBusy) await laserRef.current?.stop();
  };

  const runScrapPreview = async () => {
    const laser = laserRef.current;
    if (!laser?.connected || laser.busy || jobRef.current || startingRef.current || motionCheckBusy) return;
    if (!window.confirm("Preview fires the laser at low power (1 of 10) and traces a 30 × 10 mm box until you press Stop or 30 s pass. It can mark leather, so use scrap only. Continue?")) return;
    infoReadingRef.current = true;
    setInfoBusy(true);
    try {
      const bounds = getScrapTestPreviewBounds({ preset: presetDraft });
      await laser.startPreview({ ...bounds, power: 1, diameter: 20, maxMs: 30000 });
    } catch (error) {
      setFeedback(error.message || 'Could not start the scrap preview.');
    } finally {
      infoReadingRef.current = false;
      setInfoBusy(false);
      await refreshIdle();
      focusScan();
    }
  };

  const stopScrapPreview = async () => {
    try {
      await laserRef.current?.stopPreview();
    } catch (error) {
      setFeedback(error.message || 'Could not stop the scrap preview.');
    }
  };

  const stopCurrentOperation = async () => {
    if (job) await stopJob();
    else if (laserSnapshot.previewing) await stopScrapPreview();
    else if (motionCheckBusy) await stopMotionCheck();
  };

  const copyLaserInfo = async () => {
    try {
      await navigator.clipboard.writeText(laserInfoText);
      setCopyState('Copied');
    } catch {
      setCopyState('Copy failed');
    }
  };

  const updateItem = (key, changes) => {
    setOrders((current) => current.map((order) => ({
      ...order,
      items: order.items.map((item) => itemKey({ ...item, orderName: order.orderName }) === key
        ? { ...item, ...changes }
        : item),
    })));
  };

  const postJob = async (item, status, { attemptId, detail } = {}) => apiRequest('/jobs', {
    method: 'POST',
    body: JSON.stringify({
      station: stationName,
      orderName: item.orderName,
      lineItemId: item.lineItemId,
      unit: item.unit,
      sku: item.sku,
      text: item.text,
      font: item.font,
      status,
      ...(attemptId === undefined ? {} : { attemptId }),
      ...(detail === undefined ? {} : { detail }),
    }),
  });

  const startJob = async (type = 'engrave') => {
    const laser = laserRef.current;
    const test = type !== 'engrave';
    const item = selectedItem;
    const preset = test ? presetDraft : currentPreset;
    if (!laser?.connected || laser.busy || jobRef.current || startingRef.current || !preset || (!test && !item?.placement)) return;
    if (!test && getEngravingState(item) !== 'none'
      && !window.confirm(`Re-engrave "${item.text}"? The last attempt stays in the history.`)) return;
    startingRef.current = true;
    setStarting(true);
    try {
      if (!await laser.isFileIdle()) {
        setLaserIdle(false);
        setFeedback('The laser is not idle. Check the machine before engraving.');
        return;
      }
      let jobData;
      if (type === 'origin') {
        jobData = buildOriginTestJob({ preset });
      } else {
        const text = type === 'scrap' ? 'TEST' : item.text;
        const fontName = type === 'scrap' ? 'Montserrat' : item.font;
        const fontBuffer = await loadFontBuffer(fontName);
        const unsupported = findUnsupportedCharacter(fontBuffer, text);
        if (unsupported) throw new Error(`This font can't engrave '${unsupported}'`);
        if (!test && text.length > Number(item.placement?.laserMax)) {
          throw new Error(`Text exceeds the ${item.placement.laserMax}-character laser limit.`);
        }
        jobData = type === 'scrap'
          ? buildScrapTestJob({ fontBuffer, preset, geometryOnly: true })
          : buildJob({ fontBuffer, text, preset, geometryOnly: true });
      }
      const fillJob = buildFillJob({
        contours: jobData.canvasContours,
        preset: { ...preset, dpi: fillDpi },
        printName: item?.orderName || (type === 'scrap' ? 'SCRAP TEST' : 'ORIGIN TEST'),
      });
      const runningJob = { item, test, type, progress: 0, stopped: false };
      jobRef.current = runningJob;
      setJob(runningJob);
      setFeedback('');
      const startedAt = Date.now();
      const finalStatus = await laser.run(fillJob, (progress) => {
        const next = { ...runningJob, progress };
        jobRef.current = next;
        setJob(next);
      });
      const startWarning = finalStatus.startObserved ? '' : 'The laser never reported the job starting within 15 seconds. Check the piece before confirming the result.';
      if (startWarning) setFeedback(startWarning);
      if (!test) {
        const key = itemKey(item);
        const messages = startWarning ? [startWarning] : [];
        const detail = {
          format: '0x40',
          fileId: fillJob.fileId,
          segments: fillJob.payload.length / 6,
          dpi: fillJob.dpi,
          finalStatus,
          bounds: fillJob.bounds,
          durationMs: Date.now() - startedAt,
        };
        let attemptId = null;
        try {
          const result = await postJob(item, 'sent', { detail });
          if (!result?.id) throw new Error('Engraving attempt log did not return an id.');
          attemptId = result.id;
        } catch (_error) {
          messages.push('Engraving finished, but its attempt log did not save.');
        }
        updateItem(key, {
          engravingState: 'unconfirmed',
          attempts: (Number(item.attempts) || 0) + 1,
          lastAttemptId: attemptId,
          engravedAt: null,
        });
        setConfirmation({ key, attemptId });
        setFeedback(messages.join(' '));
      }
    } catch (error) {
      if (!jobRef.current?.stopped) setFeedback(error.message || 'Engraving failed.');
    } finally {
      startingRef.current = false;
      setStarting(false);
      jobRef.current = null;
      setJob(null);
      setLaserIdle(false);
      await refreshIdle();
      focusScan();
    }
  };

  const stopJob = async () => {
    const runningJob = jobRef.current;
    if (!runningJob) return;
    runningJob.stopped = true;
    try {
      await laserRef.current?.stop();
      if (!runningJob.test && runningJob.item) {
        const key = itemKey(runningJob.item);
        updateItem(key, {
          engravingState: 'stopped',
          attempts: (Number(runningJob.item.attempts) || 0) + 1,
          engravedAt: null,
        });
        setConfirmation(null);
        try {
          await postJob(runningJob.item, 'stopped');
        } catch (_error) {
          setFeedback('Laser stopped, but its job log could not be saved.');
        }
      }
    } catch (error) {
      setFeedback(error.message || 'Could not send the LP4 stop command. Check the machine.');
    } finally {
      focusScan();
    }
  };

  const saveAttemptResult = async (confirmed) => {
    const item = selectedItem;
    const key = item ? itemKey(item) : null;
    if (!item || !key || !activeConfirmation?.attemptId || resultSaving) return;
    setResultSaving(true);
    try {
      await postJob(item, confirmed ? 'confirmed' : 'failed', { attemptId: activeConfirmation.attemptId });
      updateItem(key, {
        engravingState: confirmed ? 'confirmed' : 'failed',
        engravedAt: confirmed ? new Date().toISOString() : null,
      });
      setConfirmation(null);
      setFeedback('');
      if (confirmed) {
        const next = items.find((candidate) =>
          itemKey(candidate) !== key && getEngravingState(candidate) !== 'confirmed'
        );
        setSelectedKey(next ? itemKey(next) : null);
      }
    } catch (error) {
      setFeedback(error.message || 'Could not save the engraving result.');
    } finally {
      setResultSaving(false);
      focusScan();
    }
  };

  const savePreset = async () => {
    if (!canEdit || !selectedItem?.handle) return;
    setSavedState('saving');
    try {
      const result = await apiRequest('/presets', {
        method: 'PUT',
        body: JSON.stringify({
          station: stationName,
          product_handle: selectedItem.handle,
          ...presetDraft,
        }),
      });
      const preset = result.preset || result;
      updateItem(itemKey(selectedItem), { preset });
      setSavedState('saved');
    } catch (error) {
      setSavedState(error.message || 'Could not save preset.');
    } finally {
      focusScan();
    }
  };

  const engraveDisabled = !selectedItem
    || !currentPreset
    || !selectedItem.placement
    || String(selectedItem.text || '').length > Number(selectedItem.placement?.laserMax)
    || !laserSnapshot.connected
    || !laserIdle
    || glyphLoading
    || Boolean(glyphError)
    || Boolean(starting)
    || Boolean(job);
  const unavailableReason = selectedItem && !currentPreset
    ? 'Save a preset before engraving.'
    : selectedItem && !selectedItem.placement
      ? 'This product has no laser placement configured.'
      : selectedItem && String(selectedItem.text || '').length > Number(selectedItem.placement?.laserMax)
        ? `Text exceeds the ${selectedItem.placement.laserMax}-character laser limit.`
        : glyphError;
  const busyLabel = job?.type === 'origin' ? 'Testing origin…' : job?.type === 'scrap' ? 'Testing on scrap…' : 'Engraving…';

  return (
    <div className="engraving-workstation flex flex-col gap-5 overflow-hidden px-7 pb-6 pt-5">
      <header className="flex h-11 flex-none items-center gap-5">
        <h1 className="w-[264px] shrink-0 text-xl font-semibold text-gray-900">Engraving</h1>
        <form onSubmit={loadTote} className="mx-auto flex w-full max-w-xl items-center gap-2">
          <label className="sr-only" htmlFor="engraving-tote-scan">Scan tote</label>
          <input ref={scanInputRef} id="engraving-tote-scan" value={barcode} onChange={(event) => setBarcode(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') loadTote(event); }}
            placeholder="Scan tote" autoComplete="off" className="engraving-scan-input h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-4 text-base" />
          <button type="submit" disabled={loading || !barcode.trim()} className="h-10 rounded-lg bg-gray-900 px-4 text-sm font-semibold text-white disabled:opacity-45">{loading ? 'Loading…' : 'Load'}</button>
        </form>
        <div className="flex shrink-0 items-center gap-3">
          <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${job ? 'bg-amber-50 text-amber-800' : laserSnapshot.connected ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
            <span className={`h-2 w-2 rounded-full ${job ? 'bg-amber-500' : laserSnapshot.connected ? 'bg-emerald-500' : 'bg-gray-400'}`} />
            {job ? busyLabel : laserSnapshot.connected ? (laserIdle ? 'Laser ready' : 'Laser connected · checking') : 'Laser not paired'}
            {!laserSnapshot.connected && <button type="button" onClick={() => pairLaser(false)} className="ml-1 underline underline-offset-2">Pair laser</button>}
          </div>
          <button type="button" onClick={() => setDrawerOpen(true)} className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50" aria-label="Station setup" title="Station setup"><Cog8ToothIcon className="h-5 w-5" /></button>
        </div>
      </header>

      {(feedback || historyUnavailable || (!laserSnapshot.connected && !navigator.serial)) && (
        <div className="-mt-2 flex flex-none flex-wrap gap-2">
          {feedback && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">{feedback}</div>}
          {historyUnavailable && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">Engraving history couldn't load; results may be missing.</div>}
          {!laserSnapshot.connected && !navigator.serial && <div className="px-1 py-2 text-sm text-gray-500">Use desktop Chrome to engrave.</div>}
        </div>
      )}

      <main className="grid min-h-0 flex-1 grid-cols-[264px_minmax(0,1fr)_288px] gap-5">
        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex-none px-5 pb-4 pt-5">
            <h2 className="truncate text-lg font-semibold text-gray-900">{tote ? tote.name : 'No tote loaded'}</h2>
            <div className="mt-1 truncate text-sm text-gray-500">
              {tote
                ? `${tote.barcode} · ${orders.length} ${orders.length === 1 ? 'order' : 'orders'} · ${engravingCount} ${engravingCount === 1 ? 'engraving' : 'engravings'}`
                : 'Scan a tote to begin'}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {orders.map((order) => (
              <div key={order.orderName} className="pb-2">
                <div className="px-5 pb-1 pt-2 text-xs font-semibold text-gray-500">{order.orderName}</div>
                {(order.items || []).map((item) => {
                  const row = { ...item, orderName: order.orderName };
                  const key = itemKey(row);
                  const selected = key === selectedKey;
                  const badge = itemBadge(item);
                  return (
                    <button key={key} type="button" onClick={() => { setSelectedKey(key); focusScan(); }}
                      className={`flex w-full items-center gap-3 border-l-4 px-4 py-2.5 text-left hover:bg-gray-50 ${selected ? 'border-gray-900 bg-gray-50' : 'border-transparent bg-white'}`}>
                      {item.previewImage
                        ? <img src={item.previewImage} alt="" className="h-10 w-14 shrink-0 rounded-md bg-gray-100 object-cover" />
                        : <div className="engraving-preview-empty h-10 w-14 shrink-0 rounded-md" />}
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-base text-gray-900 ${fontClass(item.font)}`}>{item.text}</div>
                        <span title={badge?.label} className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${badge ? badge.className : 'bg-gray-100 text-gray-600'}`}>{badge ? badge.label.split(':')[0] : 'To engrave'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
            {tote && engravingCount === 0 && <div className="px-6 py-12 text-center text-sm text-gray-500">Nothing to engrave in this tote.</div>}
          </div>
          {tote && engravingCount > 0 && (
            <div className="flex-none border-t border-gray-100 px-5 py-3 text-sm font-medium text-gray-600">
              {allConfirmed(items) ? 'All engraved. Keep packing in ShipHero.' : `${remainingCount} left in this tote`}
            </div>
          )}
        </section>

        <section className="flex min-h-0 flex-col gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          {selectedItem && selectedItem.placement && selectedItem.previewImage ? (
            <>
              <canvas ref={previewRef} className="engraving-preview-full" aria-label={`Preview of ${selectedItem.text} on the product`} />
              <div className="flex-none text-[11px] font-semibold uppercase tracking-wide text-gray-500">Close-up</div>
              <div className="min-h-0 flex-1">
                <canvas ref={closeRef} className="engraving-preview-canvas" aria-label={`Close-up of ${selectedItem.text}`} />
              </div>
            </>
          ) : (
            <div className="grid flex-1 place-items-center text-center text-sm text-gray-500">
              {selectedItem ? 'Preview unavailable' : tote ? 'Nothing to engrave in this tote.' : 'Scan a tote to begin'}
            </div>
          )}
        </section>

        <section className="flex min-h-0 flex-col overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          {selectedItem ? (
            <>
              <div className="flex justify-end">
                {(() => {
                  const badge = itemBadge(selectedItem);
                  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge ? badge.className : 'bg-gray-100 text-gray-600'}`}>{badge ? badge.label.split(':')[0] : 'To engrave'}</span>;
                })()}
              </div>
              <div className={`mt-3 break-words text-center text-4xl leading-tight text-gray-900 ${fontClass(selectedItem.font)}`}>{selectedItem.text}</div>
              <dl className="mt-6 grid grid-cols-[72px_minmax(0,1fr)] gap-x-3 gap-y-2 border-t border-gray-100 pt-5 text-sm">
                <dt className="text-gray-500">Font</dt><dd className="font-medium text-gray-900">{selectedItem.font}</dd>
                <dt className="text-gray-500">Product</dt>
                <dd className="text-gray-900"><span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full border border-black/10 align-middle" style={{ background: COLOURS[selectedItem.colour] || '#9ca3af' }} />{selectedItem.productTitle} · {selectedItem.colour}</dd>
                <dt className="text-gray-500">SKU</dt><dd className="text-gray-900">{selectedItem.sku}</dd>
                <dt className="text-gray-500">Order</dt><dd className="text-gray-900">{selectedItem.orderName}</dd>
              </dl>
              {activeConfirmation && (
                <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                  <p className="text-sm font-medium text-gray-900">Check the wallet. Did "{selectedItem.text}" come out right?</p>
                  {!activeConfirmation.attemptId && <p className="mt-2 text-sm text-amber-800">The attempt log did not save, so this result cannot be recorded.</p>}
                  <div className="mt-3 flex justify-center gap-2">
                    <button type="button" disabled={!activeConfirmation.attemptId || resultSaving} onClick={() => saveAttemptResult(true)} className="rounded-md bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">Yes, correct</button>
                    <button type="button" disabled={!activeConfirmation.attemptId || resultSaving} onClick={() => saveAttemptResult(false)} className="rounded-md border border-gray-900 bg-white px-4 py-1.5 text-sm font-semibold text-gray-900 disabled:cursor-not-allowed disabled:opacity-40">No, wrong</button>
                  </div>
                </div>
              )}
              {!activeConfirmation && getEngravingState(selectedItem) === 'failed' && (
                <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"><span className="font-semibold">Last attempt failed.</span> It stays in the history. Put a new piece under the laser and click Re-engrave.</div>
              )}
              {glyphLoading && <p className="mt-4 text-center text-sm text-gray-500">Checking font glyphs…</p>}
              {unavailableReason && <p className="mt-4 text-center text-sm text-amber-700">{unavailableReason}</p>}
              <div className="mt-auto flex flex-col gap-2 pt-5">
                <button type="button" disabled title="Physical preview isn't available yet" aria-describedby="engraving-preview-note" className="h-10 w-full rounded-lg border border-gray-900 bg-white text-sm font-semibold text-gray-900 disabled:cursor-not-allowed disabled:opacity-40">Preview</button>
                {job || motionCheckBusy || laserSnapshot.previewing
                  ? <button type="button" onClick={stopCurrentOperation} className="h-10 w-full rounded-lg bg-red-600 text-sm font-semibold text-white">Stop</button>
                  : <button type="button" disabled={engraveDisabled} onClick={() => startJob('engrave')} className="h-10 w-full rounded-lg bg-gray-900 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">{mainActionLabel(selectedItem)}</button>}
              </div>
              <p id="engraving-preview-note" className="mt-2 text-center text-xs text-gray-500">Preview unlocks after Test preview on scrap passes.</p>
            </>
          ) : (
            <div className="grid flex-1 place-items-center text-center text-sm text-gray-500">
              <div>{tote ? 'Nothing selected' : 'Scan a tote to begin'}
                {(job || motionCheckBusy || laserSnapshot.previewing) && <div><button type="button" onClick={stopCurrentOperation} className="mt-5 h-10 rounded-lg bg-red-600 px-6 text-sm font-semibold text-white">Stop</button></div>}
              </div>
            </div>
          )}
        </section>
      </main>

      {drawerOpen && (
        <div className="engraving-drawer-backdrop fixed inset-0 z-50 flex justify-end" onMouseDown={(event) => { if (event.target === event.currentTarget) setDrawerOpen(false); }}>
          <aside className="engraving-drawer flex flex-col bg-white shadow-2xl" aria-label="Station setup">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Station setup</div><h2 className="mt-1 text-lg font-semibold">Laser & preset</h2></div>
              <button type="button" onClick={() => setDrawerOpen(false)} className="rounded p-2 text-gray-500 hover:bg-gray-100" aria-label="Close setup">×</button>
            </div>
            <div className="space-y-5 overflow-y-auto px-5 py-5">
              <label className="block text-sm font-medium text-gray-700">Station name
                <input value={stationName} onChange={(event) => setStationName(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => pairLaser(false)} className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white">Pair laser</button>
                <button type="button" onClick={() => pairLaser(true)} className="text-xs text-gray-600 underline">Show all devices</button>
                <button type="button" disabled={!laserSnapshot.connected || laserSnapshot.busy || infoBusy} onClick={readLaserInfo} className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-40">{infoBusy ? 'Reading…' : 'Read $I and ?'}</button>
                <button type="button" disabled={!laserSnapshot.connected || laserSnapshot.busy || Boolean(job) || starting || infoBusy} onClick={runMotionCheck} className="rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-700 disabled:opacity-40">Motion check (laser off)</button>
                {motionCheckBusy && <button type="button" onClick={stopMotionCheck} className="rounded-md border border-red-200 px-3 py-2 text-xs font-medium text-red-700">Stop</button>}
              </div>
              {!navigator.serial && <p className="text-sm text-amber-700">Use desktop Chrome to engrave.</p>}
              {laserInfo && (
                <section className="rounded-md border border-gray-200">
                  <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                    <h3 className="text-sm font-medium text-gray-700">{Array.isArray(laserInfo.steps) ? 'Motion check (laser off)' : 'Laser info'}</h3>
                    <button type="button" onClick={copyLaserInfo} className="text-xs font-medium text-gray-600 underline">{copyState || 'Copy'}</button>
                  </div>
                  <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap break-all bg-gray-900 p-3 font-mono text-xs text-green-200">{laserInfoText}</pre>
                </section>
              )}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between">
                  <div><h3 className="font-semibold text-gray-900">{selectedItem ? selectedItem.productTitle : 'Laser test'}</h3><p className="mt-1 text-xs text-gray-500">{selectedItem ? selectedItem.handle : 'Standalone test preset'}</p></div>
                  {savedState === 'saved' && <span className="text-sm text-emerald-700">Saved ✓</span>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <label className="text-xs font-medium text-gray-600">Fill DPI (this station)
                    <select value={fillDpi} disabled={!canEdit} onChange={(event) => {
                      setFillDpi(Number(event.target.value));
                      localStorage.setItem('warehouse-engraving-fill-dpi', event.target.value);
                    }} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm">
                      <option value={254}>254 (0.1 mm)</option><option value={508}>508 (0.05 mm)</option>
                    </select>
                  </label>
                  {PRESET_FIELDS.map(([field, label, type]) => (
                    <label key={field} className={`text-xs font-medium text-gray-600 ${type === 'checkbox' ? 'flex items-center gap-2 rounded-md border border-gray-200 px-2 py-2' : ''}`}>
                      {type === 'checkbox'
                        ? <><input type="checkbox" checked={Boolean(presetDraft[field])} disabled={!canEdit} onChange={(event) => setPresetDraft((current) => ({ ...current, [field]: event.target.checked }))} />{label}</>
                        : <>{label}{type === 'select'
                          ? <select value={presetDraft[field]} disabled={!canEdit} onChange={(event) => setPresetDraft((current) => ({ ...current, [field]: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"><option value="blue">Blue</option><option value="ir">IR</option></select>
                          : <input type="number" step={field === 'line_interval_mm' ? '0.01' : '1'} value={presetDraft[field]} disabled={!canEdit} onChange={(event) => setPresetDraft((current) => ({ ...current, [field]: Number(event.target.value) }))} className="no-arrows-by-input engraving-number-input mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm" />}</>}
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">LP4 filled text · Blue 450 nm only. DPI replaces line interval; no separate outline pass. Scrap burns TEST; its preview box is a positioning guide.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={!laserSnapshot.connected || (job ? job.type !== 'origin' : (!laserIdle || starting))} onClick={() => job?.type === 'origin' ? stopJob() : startJob('origin')} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-40">{job?.type === 'origin' ? 'STOP' : 'Test origin'}</button>
              <button type="button" disabled={!laserSnapshot.connected || (job ? job.type !== 'scrap' : (!laserIdle || starting))} onClick={() => job?.type === 'scrap' ? stopJob() : startJob('scrap')} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-40">{job?.type === 'scrap' ? 'STOP' : 'Test on scrap'}</button>
              {laserSnapshot.previewing
                ? <button type="button" onClick={stopScrapPreview} className="rounded-md bg-red-600 px-3 py-2 text-xs font-medium text-white">Stop</button>
                : <button type="button" disabled={!laserSnapshot.connected || laserSnapshot.busy || Boolean(job) || starting || motionCheckBusy || infoBusy} onClick={runScrapPreview} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-medium disabled:opacity-40">Test preview on scrap</button>}
                </div>
                {laserSnapshot.previewing && <p className="mt-2 text-sm font-medium text-red-700">Previewing: laser on, low power</p>}
                {canEdit && selectedItem && <div className="mt-4 flex items-center gap-3">
                  <button type="button" disabled={savedState === 'saving' || (!draftHasChanges && savedState === 'saved')} onClick={savePreset} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{savedState === 'saving' ? 'Saving…' : 'Save preset'}</button>
                  {savedState && savedState !== 'saved' && savedState !== 'saving' && <span role="alert" className="text-xs text-red-700">{savedState}</span>}
                </div>}
                {!canEdit && <p className="mt-3 text-xs text-gray-500">View only. Warehouse engraving edit access is required to change presets.</p>}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default EngravingWorkstation;
